"use client";

import * as React from "react";
import { cn } from "../../lib/utils";
import { Typography } from "../Typography";
import {
  SWITCH_STYLES,
  switchVariants,
  THUMB_SIZE_CLASSES,
  THUMB_TRANSLATE_CLASSES,
} from "./constants";
import { SwitchProps, SwitchWithLabelProps } from "./types";

const Switch = React.forwardRef<HTMLButtonElement, SwitchProps>(
  ({ className, checked = false, onCheckedChange, disabled, size = "default", ...props }, ref) => {
    const thumbSize = size ?? "default";

    return (
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        ref={ref}
        disabled={disabled}
        onClick={() => onCheckedChange?.(!checked)}
        className={cn(
          switchVariants({ size }),
          checked ? SWITCH_STYLES.trackChecked : SWITCH_STYLES.trackUnchecked,
          className
        )}
        {...props}
      >
        <span
          className={cn(
            SWITCH_STYLES.thumb,
            THUMB_SIZE_CLASSES[thumbSize],
            checked && THUMB_TRANSLATE_CLASSES[thumbSize]
          )}
        />
      </button>
    );
  }
);
Switch.displayName = "Switch";

const SwitchWithLabel = React.forwardRef<HTMLButtonElement, SwitchWithLabelProps>(
  ({ label, id, labelColor, labelSize = "sm", labelWeight = "medium", className, ...props }, ref) => {
    const generatedId = React.useId();
    const switchId = id || generatedId;

    return (
      <div className={SWITCH_STYLES.container}>
        <Switch id={switchId} ref={ref} className={className} {...props} />
        <Typography
          as="label"
          htmlFor={switchId}
          color={labelColor}
          size={labelSize}
          weight={labelWeight}
          className="leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
        >
          {label}
        </Typography>
      </div>
    );
  }
);
SwitchWithLabel.displayName = "SwitchWithLabel";

export { Switch, SwitchWithLabel };
